import { useEffect, useContext, createContext, useMemo } from "react";
import { useContextKeycloak } from "@/app/module/keycloak/index";
import { useContextLocalization } from "@/app/module/localization/index";
import { useMenu } from "./menu-hooks";

type ContextMenuType = Omit<
  ReturnType<typeof useMenu>,
  | "fetchDefaultMenu"
  | "fetchUserMenu"
  | "fetchAdvancedMenu"
  | "fetchFooterMenuItems"
>;
export const menuContext = createContext<ContextMenuType | null>(null);

export function useContextMenu() {
  const context = useContext(menuContext);

  if (!context) {
    throw new Error("useContextMenu must be used within a ContextMenuProvider");
  }

  return context;
}

export function ContextMenuProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    fetchDefaultMenu,
    fetchUserMenu,
    fetchAdvancedMenu,
    fetchFooterMenuItems,
    menuItems,
    userMenuItems,
    footerMenuItems,
  } = useMenu();
  const { authorize, jwtToken } = useContextKeycloak();
  const { currentLang } = useContextLocalization();

  useEffect(() => {
    if (!currentLang) {
      return;
    }

    if (!jwtToken) {
      Promise.all([
        fetchDefaultMenu(currentLang, jwtToken),
        fetchFooterMenuItems(currentLang, jwtToken),
      ]).catch(console.error);
    } else {
      Promise.all([
        fetchUserMenu(currentLang, jwtToken),
        fetchAdvancedMenu(currentLang, jwtToken),
        fetchFooterMenuItems(currentLang, jwtToken),
      ]).catch(console.error);
    }
  }, [jwtToken, currentLang]);

  const menuContextValue = useMemo(
    () => ({
      menuItems,
      userMenuItems,
      footerMenuItems,
    }),
    [menuItems, userMenuItems, footerMenuItems],
  );

  if (!menuItems || (authorize && !userMenuItems)) {
    return null;
  }

  return (
    <menuContext.Provider value={menuContextValue}>
      {children}
    </menuContext.Provider>
  );
}
